<template>
	<div class="lighter-page">
		<div class="lighter-shell">
			<header class="page-heading">
				<div>
					<div class="eyebrow"><i class="bi bi-stars"></i> Plus feature</div>
					<h1>Lighten My Day</h1>
					<p>Choose a logged day and lower its calories without making it completely different.</p>
				</div>
				<div class="heading-icon"><i class="bi bi-feather"></i></div>
			</header>

			<glass-card v-if="isBasic" class="locked-state">
				<div class="lock-icon"><i class="bi bi-lock-fill"></i></div>
				<span class="plus-badge"><i class="bi bi-gem me-1"></i>Plus</span>
				<h2>Keep your day. Make it lighter.</h2>
				<p>
					Plus members can turn any day’s logged meals into a close, lower-calorie
					plan with close alternatives you can choose from.
				</p>
				<button class="primary-button" type="button" @click="unlockFeature">
					See Plus options
				</button>
			</glass-card>

			<template v-else>
				<nav class="feature-tabs" aria-label="Lighten My Day sections">
					<button :class="{ active: activeTab === 'today' }" @click="activeTab = 'today'">
						Lighten My Day
					</button>
					<button :class="{ active: activeTab === 'saved' }" @click="activeTab = 'saved'">
						Saved Plans <span>{{ savedPagination.total }}</span>
					</button>
				</nav>

				<div v-if="loading" class="loading-state">
					<div class="spinner-border" role="status"></div>
					<span>Loading your day…</span>
				</div>

				<section v-else-if="activeTab === 'saved'" class="saved-section">
					<div class="section-heading">
						<div>
							<p class="section-kicker">Your reusable ideas</p>
							<h2>Saved lighter plans</h2>
						</div>
					</div>

					<glass-card v-if="!savedPlans.length" class="empty-state">
						<i class="bi bi-bookmark-heart"></i>
						<h3>No saved plans yet</h3>
						<p>When a preview feels right, save it here to revisit later.</p>
					</glass-card>

					<div v-else class="saved-grid">
						<glass-card v-for="saved in savedPlans" :key="saved.id" class="saved-card">
							<div class="saved-card-top">
								<span><i class="bi bi-bookmark-check-fill"></i> Saved</span>
								<time>{{ formatDate(saved.date) }}</time>
							</div>
							<div v-if="editingTitleId !== saved.id" class="saved-title-row">
								<h3>{{ saved.title }}</h3>
								<button type="button" aria-label="Edit plan title" @click="startTitleEdit(saved)"><i class="bi bi-pencil"></i></button>
							</div>
							<form v-else class="title-editor" @submit.prevent="saveTitle(saved)">
								<label class="visually-hidden" :for="`saved-title-${saved.id}`">Plan title</label>
								<input :id="`saved-title-${saved.id}`" v-model="titleDraft" maxlength="80" autofocus />
								<div>
									<button type="button" :disabled="suggestingTitle" @click="suggestTitle(saved)"><i class="bi bi-stars"></i> Suggest title</button>
									<button type="submit" :disabled="renamingTitle || !titleDraft.trim()">Save</button>
									<button type="button" @click="cancelTitleEdit">Cancel</button>
								</div>
							</form>
							<div class="saved-calories">
								<strong>{{ saved.originalCalories }}</strong>
								<i class="bi bi-arrow-right"></i>
								<strong>{{ saved.lighterCalories }}</strong>
								<span>cal</span>
							</div>
							<p>{{ changeCountLabel(saved.plan) }} · {{ saved.plan.daySimilarity }}% familiar</p>
							<div class="saved-actions">
								<button type="button" @click="openSaved(saved)">Open</button>
								<button type="button" @click="duplicatePlan(saved)"><i class="bi bi-copy"></i></button>
								<button class="danger" type="button" @click="deletePlan(saved)"><i class="bi bi-trash3"></i></button>
							</div>
						</glass-card>
					</div>

					<nav v-if="savedPagination.totalPages > 1" class="saved-pagination" aria-label="Saved plans pages">
						<button
							type="button"
							:disabled="savedPagination.page <= 1 || loadingSavedPage"
							@click="changeSavedPage(savedPagination.page - 1)">
							<i class="bi bi-chevron-left"></i> Previous
						</button>
						<span>Page {{ savedPagination.page }} of {{ savedPagination.totalPages }}</span>
						<button
							type="button"
							:disabled="savedPagination.page >= savedPagination.totalPages || loadingSavedPage"
							@click="changeSavedPage(savedPagination.page + 1)">
							Next <i class="bi bi-chevron-right"></i>
						</button>
					</nav>
				</section>

				<section v-else-if="day" class="today-section">
					<div class="day-summary">
						<div>
							<span class="section-kicker">{{ formatDate(day.date) }}</span>
							<h2>Your logged day</h2>
						</div>
						<div class="day-summary-actions">
							<div class="date-navigator" aria-label="Choose a logged day">
								<button type="button" aria-label="Previous day" @click="shiftDate(-1)">
									<i class="bi bi-chevron-left"></i>
								</button>
								<label class="visually-hidden" for="lighter-day-date">Date to lighten</label>
								<input
									id="lighter-day-date"
									v-model="selectedDate"
									type="date"
									:max="todayDate"
									@change="changeDate" />
								<button type="button" aria-label="Next day" :disabled="isToday" @click="shiftDate(1)">
									<i class="bi bi-chevron-right"></i>
								</button>
							</div>
							<div class="total-badge"><strong>{{ day.totalCalories }}</strong><span>calories</span></div>
						</div>
					</div>

					<glass-card v-if="!day.items.length" class="empty-state">
						<i class="bi bi-camera"></i>
						<h3>Nothing to lighten yet</h3>
						<p>No meals or drinks were logged on {{ formatDate(day.date) }}. Choose another day{{ isToday ? " or add a meal" : "" }}.</p>
						<router-link v-if="isToday" to="/add" class="primary-button">Add a meal</router-link>
					</glass-card>

					<template v-else>
						<div :class="['meal-keep-help', { 'has-plan': currentPlan }]">
							<i class="bi bi-shield-check"></i>
							<div>
								<strong>Meals you don’t want changed</strong>
								<span>Tap “Don’t change” and the AI will leave that meal exactly as logged.</span>
							</div>
						</div>
						<div :class="['meal-strip', { 'has-plan': currentPlan }]" aria-label="Meals included in this plan">
							<article v-for="item in day.items" :key="item.id" :class="{ protected: lockedMealIds.has(item.id) }">
								<img :src="imageSource(item.img)" alt="" />
								<div><span>{{ item.mealType }}</span><strong>{{ item.name }}</strong><small>{{ item.calories }} cal</small></div>
								<button type="button" :aria-pressed="lockedMealIds.has(item.id)" @click="toggleMealLock(item.id)">
									<i :class="lockedMealIds.has(item.id) ? 'bi bi-shield-check' : 'bi bi-shield' "></i>
									{{ lockedMealIds.has(item.id) ? "Won’t change" : "Don’t change" }}
								</button>
							</article>
						</div>

						<glass-card class="target-card">
							<div class="target-heading">
								<div>
									<span class="section-kicker">AI suggestions</span>
									<h2>Find lighter options</h2>
									<p>We’ll suggest close changes for each meal. Add only the ones you like.</p>
								</div>
							</div>
							<button class="primary-button generate-button" type="button" :disabled="generating || !day.maxTarget" @click="generatePlan">
								<span v-if="generating" class="spinner-border spinner-border-sm me-2"></span>
								<i v-else class="bi bi-stars me-2"></i>
								{{ generating ? "Finding close alternatives…" : currentPlan ? "Refresh suggestions" : "Find lighter options" }}
							</button>
						</glass-card>

						<div v-if="currentPlan" class="plan-area">
							<glass-card class="result-summary">
								<div class="result-heading">
									<div class="preview-label"><i class="bi bi-shield-check"></i> Preview only · your log stays unchanged</div>
									<button v-if="editingTitleId !== currentPlan.id" type="button" aria-label="Edit plan title" @click="startTitleEdit(currentPlan)"><i class="bi bi-pencil"></i> Edit title</button>
								</div>
								<h3 v-if="editingTitleId !== currentPlan.id" class="plan-title">{{ currentPlan.title }}</h3>
								<form v-else class="title-editor current-title-editor" @submit.prevent="saveTitle(currentPlan)">
									<label class="visually-hidden" for="current-plan-title">Plan title</label>
									<input id="current-plan-title" v-model="titleDraft" maxlength="80" autofocus />
									<div>
										<button type="button" :disabled="suggestingTitle" @click="suggestTitle(currentPlan)"><i class="bi bi-stars"></i> Suggest title</button>
										<button type="submit" :disabled="renamingTitle || !titleDraft.trim()">Save</button>
										<button type="button" @click="cancelTitleEdit">Cancel</button>
									</div>
								</form>
								<div class="calorie-result">
									<div><strong>{{ currentPlan.originalCalories }}</strong><span>Logged day</span></div>
									<i class="bi bi-arrow-right"></i>
									<div class="lighter-total"><strong>{{ currentPlan.lighterCalories }}</strong><span>Lighter plan</span></div>
									<div class="saved-total">−{{ currentPlan.plan.totalSavings }} cal</div>
								</div>
								<p>{{ currentPlan.plan.summary }}</p>
							</glass-card>

							<div class="plan-columns">
								<div class="change-list">
									<div class="section-heading compact"><div><span class="section-kicker">Add the ones you like</span><h2>Suggested changes</h2></div></div>
									<article
										v-for="change in currentPlan.plan.changes"
										:key="change.id"
										:class="['change-card', { active: selectedChange?.id === change.id, locked: change.locked }]"
										tabindex="0"
										@click="selectedChangeId = change.id"
										@keydown.enter="selectedChangeId = change.id">
										<span class="rank">
											<i v-if="change.locked" class="bi bi-plus-lg"></i>
											<template v-else>{{ activeChangeRank(currentPlan.plan, change) }}</template>
										</span>
										<div class="change-copy"><small>{{ change.mealType }} · {{ change.itemName }}</small><strong>{{ change.label }}</strong><span>{{ change.original }} → {{ change.proposed }}</span></div>
										<div class="change-choice">
											<div class="change-saving"><strong>{{ change.savings }}</strong><span>cal saved</span></div>
											<button type="button" :class="{ approved: !change.locked }" @click.stop="toggleChangeApproval(change)">
												<i :class="change.locked ? 'bi bi-plus-lg' : 'bi bi-check-lg'"></i>
												{{ change.locked ? "Add" : "Added" }}
											</button>
										</div>
									</article>
								</div>

								<div v-if="selectedChange" class="change-detail">
									<div class="detail-photo" :style="{ backgroundImage: `url(${changeImage(selectedChange)})` }">
										<span>{{ selectedChange.similarityScore }}% familiar</span>
									</div>
									<div class="detail-content">
										<div class="detail-title"><div><span class="section-kicker">Selected suggestion</span><h2>{{ selectedChange.label }}</h2></div><button type="button" :class="{ active: !selectedChange.locked }" :aria-pressed="!selectedChange.locked" @click="toggleChangeApproval(selectedChange)"><i :class="selectedChange.locked ? 'bi bi-plus-lg' : 'bi bi-check-lg'"></i> {{ selectedChange.locked ? "Add change" : "Added" }}</button></div>
										<div class="swap-line"><div><span>What you had</span><strong>{{ selectedChange.original }}</strong><small>{{ selectedChange.originalCalories }} cal</small></div><i class="bi bi-arrow-right"></i><div><span>Lighter version</span><strong>{{ selectedChange.proposed }}</strong><small>{{ selectedChange.proposedCalories }} cal</small></div></div>
										<p class="reason"><i class="bi bi-lightbulb"></i>{{ selectedChange.reason }}</p>
										<div v-if="selectedChange.alternatives?.length" class="alternatives">
											<h3>Close alternatives</h3>
											<button v-for="(alternative, index) in selectedChange.alternatives" :key="`${selectedChange.id}-${index}`" type="button" @click="chooseAlternative(selectedChange, index)">
												<span><strong>{{ alternative.label }}</strong><small>{{ alternative.proposed }}</small></span><span>−{{ alternative.savings }} cal <i class="bi bi-chevron-right"></i></span>
											</button>
										</div>
									</div>
								</div>
							</div>
						</div>
					</template>
				</section>
			</template>
		</div>

		<div v-if="!isBasic && currentPlan && activeTab === 'today'" class="plan-toolbar">
			<div><span>{{ currentPlan.status === 'saved' ? "Saved plan" : `${activeChangeCount(currentPlan.plan)} selected` }}</span><strong>{{ currentPlan.plan.totalSavings }} calories lighter</strong></div>
			<div>
				<button class="secondary-button" type="button" @click="resetPreview">{{ currentPlan.status === 'saved' ? "Start new" : "Reset" }}</button>
				<button v-if="currentPlan.status !== 'saved'" class="primary-button" type="button" :disabled="saving || activeChangeCount(currentPlan.plan) === 0" :title="activeChangeCount(currentPlan.plan) === 0 ? 'Add at least one suggestion first' : ''" @click="savePlan"><span v-if="saving" class="spinner-border spinner-border-sm me-2"></span><i v-else class="bi bi-bookmark-fill me-2"></i>Save plan</button>
				<button v-else class="primary-button" type="button" @click="activeTab = 'saved'"><i class="bi bi-bookmark-check-fill me-2"></i>View saved plans</button>
			</div>
		</div>

		<div v-if="error" class="error-toast" role="alert"><i class="bi bi-exclamation-triangle-fill"></i>{{ error }}<button @click="error = ''"><i class="bi bi-x-lg"></i></button></div>
	</div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue"
import { useRoute } from "vue-router"
import GlassCard from "@/components/GlassCard.vue"
import { useUserStore } from "@/stores/user"
import { fetchWithAuth } from "@/utils/api-client"
import { getImageSource } from "@/utils/mealLib"
import * as SubscriptionHelper from "@/utils/subscriptionHelper"
import pizzaImage from "@/assets/pizza-info.jpg"
import saladImage from "@/assets/salad1.png"

const userStore = useUserStore()
const route = useRoute()
const isDesignPreview = computed(
	() => import.meta.env.DEV && route.query.designPreview === "1",
)
const isBasic = computed(
	() =>
		!isDesignPreview.value &&
		(userStore.user?.subscription_type ?? "basic") === "basic",
)
const activeTab = ref("today")
const loading = ref(false)
const loadingSavedPage = ref(false)
const generating = ref(false)
const saving = ref(false)
const error = ref("")
const day = ref(null)
const savedPlans = ref([])
const savedPagination = ref({ page: 1, pageSize: 12, total: 0, totalPages: 1 })
const currentPlan = ref(null)
const selectedChangeId = ref(null)
const lockedMealIds = ref(new Set())
const editingTitleId = ref(null)
const titleDraft = ref("")
const renamingTitle = ref(false)
const suggestingTitle = ref(false)
const todayDate = localDate()
const selectedDate = ref(todayDate)
let loadedDate = null

const selectedChange = computed(() =>
	currentPlan.value?.plan?.changes?.find((change) => change.id === selectedChangeId.value) ||
	currentPlan.value?.plan?.changes?.[0] || null,
)
const isToday = computed(() => selectedDate.value === todayDate)

function localDate() {
	const now = new Date()
	return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`
}

async function api(url, options = {}) {
	const response = await fetchWithAuth(url, {
		...options,
		headers: {
			"Content-Type": "application/json",
			"x-timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
			...(options.headers || {}),
		},
	})
	if (!(await SubscriptionHelper.requestCheckSubscription(response, "Lighten My Day"))) return null
	const data = await response.json()
	if (!response.ok) throw new Error(data.error || "Something went wrong.")
	return data
}

async function fetchSavedPage(page = savedPagination.value.page) {
	loadingSavedPage.value = true
	try {
		if (isDesignPreview.value) return
		const data = await api(
			`/api/lighter-day?date=${selectedDate.value}&savedPage=${page}&savedPageSize=${savedPagination.value.pageSize}&refresh=${Date.now()}`,
		)
		if (!data) return
		savedPlans.value = data.savedPlans || []
		savedPagination.value = data.savedPagination || savedPagination.value
	} finally {
		loadingSavedPage.value = false
	}
}

async function changeSavedPage(page) {
	if (
		page < 1 ||
		page > savedPagination.value.totalPages ||
		page === savedPagination.value.page
	) return
	error.value = ""
	try {
		await fetchSavedPage(page)
	} catch (requestError) {
		error.value = requestError.message
	}
}

async function loadPage(force = false) {
	const requestedDate = selectedDate.value
	if (isBasic.value || (!force && loadedDate === requestedDate)) return
	loading.value = true
	error.value = ""
	try {
		if (isDesignPreview.value) {
			loadDesignPreview()
			loadedDate = requestedDate
			return
		}
		const data = await api(
			`/api/lighter-day?date=${requestedDate}&savedPage=${savedPagination.value.page}&savedPageSize=${savedPagination.value.pageSize}&refresh=${Date.now()}`,
		)
		if (!data) return
		day.value = data.day
		savedPlans.value = data.savedPlans || []
		savedPagination.value = data.savedPagination || savedPagination.value
		currentPlan.value = data.draft
		selectedChangeId.value = data.draft?.plan?.changes?.[0]?.id || null
		lockedMealIds.value = new Set()
		loadedDate = requestedDate
	} catch (requestError) {
		error.value = requestError.message
	} finally {
		loading.value = false
	}
}

function loadDesignPreview() {
	if (!userStore.user) {
		userStore.setUser({ subscription_type: "plus", onboarding_completed: 1 })
	}
	const items = [
		{ id: 101, mealType: "breakfast", name: "Egg sandwich", calories: 520, img: saladImage },
		{ id: 102, mealType: "lunch", name: "Burger and fries", calories: 980, img: pizzaImage },
		{ id: 103, mealType: "dinner", name: "Chicken pasta", calories: 740, img: saladImage },
	]
	day.value = {
		date: selectedDate.value,
		items,
		totalCalories: 2240,
		maxTarget: 600,
		suggestedSavings: 400,
	}
	currentPlan.value = {
		id: "design-preview",
		date: selectedDate.value,
		title: "My lighter day",
		status: "draft",
		originalCalories: 2240,
		lighterCalories: 2240,
		sourceSnapshot: { items },
		plan: {
			totalSavings: 0,
			activeChangeCount: 0,
			daySimilarity: 100,
			summary: "Choose the close changes you want to add to your lighter day.",
			changes: [
				{
					id: "preview-1", itemId: 102, mealType: "lunch", itemName: "Burger and fries", label: "Choose a smaller side", category: "smaller_side", original: "Large fries", proposed: "Small fries", originalCalories: 410, proposedCalories: 240, savings: 170, reason: "Same side and flavor, just a portion that fits the lighter target.", similarityScore: 94, locked: true,
					alternatives: [
						{ label: "Share the fries", proposed: "Half of the large fries", proposedCalories: 205, savings: 205, reason: "Same order, smaller amount." },
						{ label: "Keep a handful", proposed: "About one-third of the large fries", proposedCalories: 140, savings: 270, reason: "Keeps the taste while reducing the portion." },
					],
				},
				{ id: "preview-2", itemId: 101, mealType: "breakfast", itemName: "Egg sandwich", label: "Go easy on the sauce", original: "Full creamy sauce", proposed: "Half the sauce", originalCalories: 150, proposedCalories: 70, savings: 80, reason: "Keeps the sandwich familiar with less added sauce.", similarityScore: 91, locked: true, alternatives: [] },
				{ id: "preview-3", itemId: 103, mealType: "dinner", itemName: "Chicken pasta", label: "Leave a little pasta", original: "Full pasta portion", proposed: "Three-quarter portion", originalCalories: 620, proposedCalories: 440, savings: 180, reason: "The meal stays the same; only the portion changes.", similarityScore: 88, locked: true, alternatives: [] },
			],
		},
	}
	savedPlans.value = [
		{
			...JSON.parse(JSON.stringify(currentPlan.value)),
			id: "saved-design-preview",
			title: "Easy weekday lighter day",
			status: "saved",
		},
	]
	savedPagination.value = { page: 1, pageSize: 12, total: 1, totalPages: 1 }
	selectedChangeId.value = "preview-1"
}

watch(() => userStore.user?.subscription_type, () => loadPage())
onMounted(loadPage)

function unlockFeature() {
	SubscriptionHelper.showLockedModal("Lighten My Day")
}

async function changeDate() {
	if (!selectedDate.value) {
		selectedDate.value = todayDate
	}
	if (selectedDate.value > todayDate) {
		selectedDate.value = todayDate
	}
	await loadPage(true)
}

async function shiftDate(days) {
	const date = new Date(`${selectedDate.value}T12:00:00`)
	date.setDate(date.getDate() + days)
	const nextDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
	if (nextDate > todayDate) return
	selectedDate.value = nextDate
	await loadPage(true)
}

function toggleMealLock(itemId) {
	const next = new Set(lockedMealIds.value)
	next.has(itemId) ? next.delete(itemId) : next.add(itemId)
	lockedMealIds.value = next
}

async function generatePlan() {
	generating.value = true
	error.value = ""
	try {
		const data = await api("/api/lighter-day/generate", {
			method: "POST",
			body: JSON.stringify({ date: day.value.date, lockedItemIds: [...lockedMealIds.value] }),
		})
		if (data) replaceCurrentPlan(data.plan)
	} catch (requestError) {
		error.value = requestError.message
	} finally {
		generating.value = false
	}
}

function replaceCurrentPlan(plan) {
	currentPlan.value = plan
	selectedChangeId.value = plan?.plan?.changes?.find((change) => change.id === selectedChangeId.value)?.id || plan?.plan?.changes?.[0]?.id || null
	if (plan?.status === "saved") {
		const index = savedPlans.value.findIndex((saved) => saved.id === plan.id)
		index >= 0 ? savedPlans.value.splice(index, 1, plan) : savedPlans.value.unshift(plan)
	}
}

async function updatePlan(body) {
	const data = await api(`/api/lighter-day/${currentPlan.value.id}`, { method: "PATCH", body: JSON.stringify(body) })
	if (data) {
		replaceCurrentPlan(data.plan)
		if (data.plan.status === "saved") await fetchSavedPage(1)
	}
}

function startTitleEdit(plan) {
	editingTitleId.value = plan.id
	titleDraft.value = plan.title || ""
}

function cancelTitleEdit() {
	editingTitleId.value = null
	titleDraft.value = ""
}

async function saveTitle(plan) {
	const title = titleDraft.value.trim()
	if (!title || renamingTitle.value) return
	renamingTitle.value = true
	error.value = ""
	try {
		const data = await api(`/api/lighter-day/${plan.id}`, {
			method: "PATCH",
			body: JSON.stringify({ action: "rename", title }),
		})
		if (!data) return
		if (currentPlan.value?.id === data.plan.id) replaceCurrentPlan(data.plan)
		const index = savedPlans.value.findIndex((saved) => saved.id === data.plan.id)
		if (index >= 0) savedPlans.value.splice(index, 1, data.plan)
		cancelTitleEdit()
		if (data.plan.status === "saved") await fetchSavedPage(1)
	} catch (requestError) {
		error.value = requestError.message
	} finally {
		renamingTitle.value = false
	}
}

async function suggestTitle(plan) {
	if (suggestingTitle.value) return
	suggestingTitle.value = true
	error.value = ""
	try {
		const data = await api(`/api/lighter-day/${plan.id}/suggest-title`, {
			method: "POST",
			body: "{}",
		})
		if (data?.title) titleDraft.value = data.title
	} catch (requestError) {
		error.value = requestError.message
	} finally {
		suggestingTitle.value = false
	}
}

async function toggleChangeApproval(change) {
	try { await updatePlan({ action: "toggle-approval", changeId: change.id }) } catch (requestError) { error.value = requestError.message }
}

async function chooseAlternative(change, alternativeIndex) {
	try { await updatePlan({ action: "choose-alternative", changeId: change.id, alternativeIndex }) } catch (requestError) { error.value = requestError.message }
}

async function savePlan() {
	saving.value = true
	error.value = ""
	try {
		const data = await api(`/api/lighter-day/${currentPlan.value.id}/save`, { method: "POST", body: JSON.stringify({ title: currentPlan.value.title }) })
		if (data) {
			replaceCurrentPlan(data.plan)
			await fetchSavedPage(1)
		}
	} catch (requestError) { error.value = requestError.message } finally { saving.value = false }
}

async function resetPreview() {
	if (currentPlan.value.status === "draft") {
		try { await api(`/api/lighter-day/${currentPlan.value.id}`, { method: "DELETE" }) } catch (requestError) { error.value = requestError.message; return }
	}
	currentPlan.value = null
	selectedChangeId.value = null
}

async function openSaved(saved) {
	selectedDate.value = saved.date
	await loadPage(true)
	replaceCurrentPlan(saved)
	activeTab.value = "today"
}

async function duplicatePlan(saved) {
	try {
		const data = await api(`/api/lighter-day/${saved.id}/duplicate`, { method: "POST", body: "{}" })
		if (data) {
			selectedDate.value = data.plan.date
			await loadPage(true)
			replaceCurrentPlan(data.plan)
			activeTab.value = "today"
		}
	} catch (requestError) { error.value = requestError.message }
}

async function deletePlan(saved) {
	if (!window.confirm(`Delete “${saved.title}”?`)) return
	try {
		await api(`/api/lighter-day/${saved.id}`, { method: "DELETE" })
		if (currentPlan.value?.id === saved.id) currentPlan.value = null
		const nextPage =
			savedPlans.value.length === 1 && savedPagination.value.page > 1
				? savedPagination.value.page - 1
				: savedPagination.value.page
		await fetchSavedPage(nextPage)
	} catch (requestError) { error.value = requestError.message }
}

function changeImage(change) {
	const item = day.value?.items?.find((candidate) => candidate.id === change.itemId) || currentPlan.value?.sourceSnapshot?.items?.find((candidate) => candidate.id === change.itemId)
	return imageSource(item?.img)
}

function imageSource(img) {
	if (typeof img === "string" && (/^(data:|blob:|https?:|\/)/.test(img))) return img
	return getImageSource(img)
}

function changeCountLabel(plan) {
	const count = activeChangeCount(plan)
	return `${count} small ${count === 1 ? "change" : "changes"}`
}

function activeChangeCount(plan) {
	return (plan?.changes || []).filter(
		(change) => !change.locked && Number(change.savings) > 0,
	).length
}

function activeChangeRank(plan, selectedChange) {
	return (plan?.changes || [])
		.filter((change) => !change.locked && Number(change.savings) > 0)
		.findIndex((change) => change.id === selectedChange.id) + 1
}

function formatDate(value) {
	if (!value) return ""
	return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(new Date(`${value}T12:00:00`))
}
</script>

<style scoped>
.lighter-page { min-height: 100%; padding: 2rem 1rem 8rem; color: #ff6400; }
.lighter-shell { width: min(1120px, 100%); margin: 0 auto; padding: 1.5rem; border: 1px solid rgba(255,255,255,.2); border-radius: 1.5rem; background: rgba(255,255,255,.08); box-shadow: 0 20px 60px rgba(0,0,0,.24), inset 0 1px 0 rgba(255,255,255,.08); backdrop-filter: blur(16px) saturate(140%); -webkit-backdrop-filter: blur(16px) saturate(140%); }
.page-heading { display: flex; justify-content: space-between; gap: 1rem; align-items: center; margin-bottom: 1.5rem; }
.page-heading h1 { margin: .25rem 0; font-size: clamp(2rem, 6vw, 3.5rem); font-weight: 900; letter-spacing: -.055em; }
.page-heading p { max-width: 42rem; margin: 0; color: #94a3b8; font-size: 1.05rem; }
.eyebrow, .section-kicker { color: #86efac; font-size: .75rem; font-weight: 900; letter-spacing: .08em; text-transform: uppercase; }
.heading-icon { display: grid; place-items: center; width: 4.75rem; height: 4.75rem; flex: 0 0 auto; border-radius: 1.5rem; color: #86efac; background: rgba(34,197,94,.14); font-size: 2rem; transform: rotate(-8deg); }
.feature-tabs { display: flex; gap: .35rem; width: fit-content; margin-bottom: 1.5rem; padding: .3rem; border: 1px solid rgba(148,163,184,.18); border-radius: .9rem; background: rgba(15,23,42,.72); }
.feature-tabs button { border: 0; border-radius: .65rem; padding: .62rem .9rem; color: #94a3b8; background: transparent; font-weight: 800; }
.feature-tabs button.active { color: #052e16; background: #86efac; }
.feature-tabs span { display: inline-grid; place-items: center; min-width: 1.25rem; margin-left: .25rem; padding: .05rem .3rem; border-radius: 999px; color: inherit; background: rgba(15,23,42,.16); font-size: .7rem; }
.locked-state, .empty-state { display: grid; justify-items: center; max-width: 42rem; margin: 2rem auto; padding: 3rem 2rem; text-align: center; background: linear-gradient(145deg, rgba(22,101,52,.24), rgba(15,23,42,.86)) !important; }
.locked-state h2, .empty-state h3 { margin: 1rem 0 .55rem; font-weight: 900; }
.locked-state p, .empty-state p { max-width: 32rem; color: #94a3b8; }
.lock-icon, .empty-state > i { display: grid; place-items: center; width: 4.5rem; height: 4.5rem; border-radius: 1.3rem; color: #86efac; background: rgba(34,197,94,.16); font-size: 1.8rem; }
.plus-badge { margin-top: 1rem; color: #86efac; font-size: .75rem; font-weight: 900; text-transform: uppercase; }
.primary-button, .secondary-button { display: inline-flex; align-items: center; justify-content: center; gap: .35rem; border: 0; border-radius: .8rem; padding: .72rem 1rem; text-decoration: none; font-weight: 900; }
.primary-button { color: #052e16; background: #86efac; }
.primary-button:hover { color: #052e16; background: #bbf7d0; }
.primary-button:disabled { opacity: .55; }
.secondary-button { border: 1px solid rgba(148,163,184,.28); color: #e2e8f0; background: rgba(30,41,59,.82); }
.loading-state { display: flex; align-items: center; justify-content: center; gap: .8rem; min-height: 18rem; color: #cbd5e1; }
.day-summary, .section-heading, .target-heading, .detail-title { display: flex; align-items: center; justify-content: space-between; gap: 1rem; }
.day-summary h2, .section-heading h2, .target-heading h2, .detail-title h2 { margin: .15rem 0 0; font-weight: 900; letter-spacing: -.025em; }
.day-summary-actions { display: flex; align-items: center; gap: .65rem; }
.date-navigator { display: grid; grid-template-columns: 2.35rem minmax(8.5rem, auto) 2.35rem; align-items: stretch; overflow: hidden; border: 1px solid rgba(148,163,184,.25); border-radius: .85rem; background: rgba(15,23,42,.78); }
.date-navigator button { border: 0; color: #cbd5e1; background: rgba(30,41,59,.7); }
.date-navigator button:hover:not(:disabled) { color: #052e16; background: #86efac; }
.date-navigator button:disabled { color: #475569; cursor: not-allowed; }
.date-navigator input { min-width: 0; border: 0; border-inline: 1px solid rgba(148,163,184,.18); padding: .55rem .65rem; color: #ff6400; color-scheme: dark; background: transparent; font-weight: 700; }
.date-navigator input:focus { outline: 2px solid #4ade80; outline-offset: -2px; }
.total-badge { display: flex; align-items: baseline; gap: .35rem; padding: .65rem .85rem; border-radius: .85rem; background: rgba(30,41,59,.74); }
.total-badge strong { font-size: 1.35rem; }.total-badge span { color: #94a3b8; font-size: .75rem; }
.meal-strip { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: .75rem; margin: 1rem 0; scrollbar-width: none; }
.meal-strip::-webkit-scrollbar { display: none; }
.meal-keep-help { display: flex; align-items: flex-start; gap: .65rem; margin-top: 1rem; padding: .75rem .85rem; border: 1px solid rgba(134,239,172,.22); border-radius: .9rem; color: #cbd5e1; background: rgba(5,46,22,.3); }
.meal-keep-help > i { color: #86efac; font-size: 1rem; }
.meal-keep-help > div { display: grid; gap: .1rem; }
.meal-keep-help strong { color: #ff6400; font-size: .82rem; }
.meal-keep-help span { color: #94a3b8; font-size: .76rem; }
.meal-strip article { display: grid; grid-template-columns: 3.1rem 1fr auto; gap: .7rem; align-items: center; min-width: 0; padding: .65rem; border: 1px solid rgba(148,163,184,.18); border-radius: 1rem; background: rgba(15,23,42,.72); }
.meal-strip article.protected { border-color: rgba(134,239,172,.5); }.meal-strip img { width: 3.1rem; height: 3.1rem; border-radius: .75rem; object-fit: cover; }
.meal-strip article div { display: grid; min-width: 0; }.meal-strip article div span { color: #86efac; font-size: .64rem; font-weight: 900; text-transform: uppercase; }.meal-strip article div strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }.meal-strip article div small { color: #94a3b8; }
.meal-strip article button { border: 0; padding: .35rem; color: #94a3b8; background: transparent; font-size: .72rem; }.meal-strip article.protected button { color: #86efac; }
.target-card { margin: 1rem 0 1.25rem; padding: 1.25rem; background: linear-gradient(145deg, rgba(22,101,52,.2), rgba(15,23,42,.84)) !important; }
.target-heading p { margin: .35rem 0 0; color: #94a3b8; }
.generate-button { width: 100%; margin-top: 1rem; padding-block: .85rem; }
.result-summary { padding: 1.25rem; background: linear-gradient(120deg, rgba(5,46,22,.94), rgba(15,23,42,.9)) !important; }.result-heading { display: flex; align-items: center; justify-content: space-between; gap: 1rem; }.result-heading > button { border: 1px solid rgba(134,239,172,.3); border-radius: .6rem; padding: .35rem .55rem; color: #86efac; background: rgba(5,46,22,.45); font-size: .72rem; font-weight: 800; }.preview-label { color: #86efac; font-size: .72rem; font-weight: 800; }.plan-title { margin: .65rem 0 0; font-size: 1.15rem; font-weight: 900; }.calorie-result { display: flex; align-items: center; gap: 1rem; margin: 1rem 0; }.calorie-result > div:not(.saved-total) { display: grid; }.calorie-result strong { font-size: clamp(1.8rem, 5vw, 2.8rem); line-height: 1; }.calorie-result span { color: #94a3b8; font-size: .75rem; }.calorie-result .lighter-total strong, .saved-total { color: #86efac; }.saved-total { margin-left: auto; padding: .5rem .7rem; border-radius: 999px; background: rgba(34,197,94,.14); font-weight: 900; }.result-summary p { margin: 0; color: #cbd5e1; }
.plan-columns { display: grid; grid-template-columns: minmax(280px,.86fr) minmax(0,1.4fr); gap: 1rem; margin-top: 1rem; }.section-heading.compact { margin: .4rem 0 .75rem; }.section-heading.compact h2 { font-size: 1.25rem; }
.change-list { min-width: 0; }.change-card { display: grid; grid-template-columns: 1.75rem minmax(0,1fr) auto; align-items: center; gap: .65rem; width: 100%; margin-bottom: .55rem; padding: .85rem; border: 1px solid rgba(148,163,184,.17); border-radius: 1rem; text-align: left; color: #f8fafc; background: rgba(15,23,42,.75); cursor: pointer; }.change-card.active { border-color: #4ade80; background: rgba(5,46,22,.7); }.change-card.locked .rank { color: #86efac; background: rgba(34,197,94,.16); }.rank { display: grid; place-items: center; width: 1.7rem; height: 1.7rem; border-radius: .55rem; color: #052e16; background: #86efac; font-weight: 900; }.change-copy { display: grid; min-width: 0; }.change-copy small { color: #86efac; text-transform: capitalize; }.change-copy strong { margin: .1rem 0; }.change-copy span { overflow: hidden; color: #94a3b8; font-size: .75rem; text-overflow: ellipsis; white-space: nowrap; }.change-choice { display: grid; justify-items: end; gap: .3rem; }.change-saving { display: flex; align-items: baseline; gap: .25rem; text-align: right; }.change-saving strong { color: #86efac; font-size: 1.2rem; }.change-saving span { color: #64748b; font-size: .65rem; text-transform: uppercase; }.change-choice button { display: inline-flex; align-items: center; gap: .25rem; border: 1px solid rgba(134,239,172,.35); border-radius: .55rem; padding: .28rem .48rem; color: #86efac; background: rgba(5,46,22,.45); font-size: .7rem; font-weight: 800; }.change-choice button.approved { color: #052e16; background: #86efac; }
.change-detail { overflow: hidden; border: 1px solid rgba(148,163,184,.2); border-radius: 1.35rem; background: rgba(15,23,42,.8); }.detail-photo { display: flex; align-items: flex-end; min-height: 11rem; padding: 1rem; background-position: center; background-size: cover; position: relative; }.detail-photo::after { content: ""; position: absolute; inset: 0; background: linear-gradient(transparent, rgba(2,6,23,.85)); }.detail-photo span { position: relative; z-index: 1; padding: .35rem .6rem; border-radius: 999px; color: #052e16; background: #86efac; font-size: .72rem; font-weight: 900; }.detail-content { padding: 1.15rem; }.detail-title button { border: 1px solid rgba(148,163,184,.24); border-radius: .7rem; padding: .45rem .65rem; color: #cbd5e1; background: rgba(30,41,59,.8); font-size: .72rem; }.detail-title button.active { border-color: #86efac; color: #86efac; }.swap-line { display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; gap: .8rem; margin: 1rem 0; padding: .9rem; border-radius: 1rem; background: rgba(2,6,23,.38); }.swap-line > div { display: grid; }.swap-line span { color: #64748b; font-size: .68rem; text-transform: uppercase; }.swap-line small { color: #86efac; }.reason { display: flex; gap: .55rem; color: #cbd5e1; font-size: .85rem; }.reason i { color: #fbbf24; }.alternatives { margin: 1rem 0; }.alternatives h3 { font-size: .88rem; font-weight: 900; }.alternatives button { display: flex; align-items: center; justify-content: space-between; gap: .8rem; width: 100%; margin-top: .45rem; padding: .7rem .75rem; border: 1px solid rgba(148,163,184,.17); border-radius: .8rem; color: #f8fafc; background: rgba(30,41,59,.65); text-align: left; }.alternatives button > span:first-child { display: grid; }.alternatives small { color: #94a3b8; }.alternatives button > span:last-child { flex: 0 0 auto; color: #86efac; font-size: .75rem; font-weight: 800; }
.saved-section { min-height: 24rem; }.saved-grid { display: grid; grid-template-columns: repeat(auto-fit,minmax(260px,1fr)); gap: 1rem; margin-top: 1rem; }.saved-card { padding: 1.15rem; background: rgba(15,23,42,.78) !important; }.saved-card-top { display: flex; justify-content: space-between; color: #86efac; font-size: .72rem; font-weight: 800; }.saved-card-top time { color: #64748b; }.saved-title-row { display: flex; align-items: center; justify-content: space-between; gap: .7rem; }.saved-card h3 { margin: 1rem 0; font-weight: 900; }.saved-title-row > button { flex: 0 0 auto; border: 1px solid rgba(148,163,184,.25); border-radius: .55rem; padding: .35rem .45rem; color: #94a3b8; background: rgba(30,41,59,.7); }.saved-title-row > button:hover { border-color: #86efac; color: #86efac; }.title-editor { display: grid; gap: .55rem; margin: .8rem 0; }.title-editor input { width: 100%; border: 1px solid rgba(134,239,172,.35); border-radius: .7rem; padding: .6rem .7rem; color: #f8fafc; background: rgba(2,6,23,.55); }.title-editor > div { display: flex; flex-wrap: wrap; gap: .4rem; }.title-editor button { border: 1px solid rgba(148,163,184,.25); border-radius: .6rem; padding: .4rem .58rem; color: #cbd5e1; background: rgba(30,41,59,.75); font-size: .72rem; font-weight: 800; }.title-editor button[type="submit"] { border-color: #86efac; color: #052e16; background: #86efac; }.title-editor button:disabled { opacity: .5; }.current-title-editor { margin-bottom: 0; }.saved-calories { display: flex; gap: .55rem; align-items: baseline; }.saved-calories strong { font-size: 1.45rem; }.saved-calories strong:last-of-type { color: #86efac; }.saved-calories span,.saved-card p { color: #94a3b8; font-size: .78rem; }.saved-card p { margin: .5rem 0 1rem; }.saved-actions { display: grid; grid-template-columns: 1fr auto auto; gap: .4rem; }.saved-actions button { border: 0; border-radius: .65rem; padding: .55rem .7rem; color: #052e16; background: #86efac; font-weight: 800; }.saved-actions button:not(:first-child) { color: #cbd5e1; background: rgba(51,65,85,.75); }.saved-actions button.danger { color: #fca5a5; }
.saved-pagination { display: flex; align-items: center; justify-content: center; gap: 1rem; margin-top: 1.25rem; }
.saved-pagination button { display: inline-flex; align-items: center; gap: .35rem; border: 1px solid rgba(148,163,184,.28); border-radius: .75rem; padding: .58rem .8rem; color: #e2e8f0; background: rgba(30,41,59,.82); font-weight: 800; }
.saved-pagination button:hover:not(:disabled) { border-color: #86efac; color: #86efac; }
.saved-pagination button:disabled { opacity: .4; cursor: not-allowed; }
.saved-pagination span { color: #94a3b8; font-size: .82rem; font-weight: 700; }
.plan-toolbar { position: fixed; z-index: 20; left: 50%; bottom: 1rem; transform: translateX(-50%); display: flex; align-items: center; justify-content: space-between; gap: 1rem; width: min(760px,calc(100% - 2rem)); padding: .75rem; border: 1px solid rgba(134,239,172,.28); border-radius: 1rem; background: rgba(2,6,23,.92); box-shadow: 0 20px 60px rgba(0,0,0,.42); backdrop-filter: blur(18px); }.plan-toolbar > div:first-child { display: grid; }.plan-toolbar > div:first-child span { color: #86efac; font-size: .67rem; font-weight: 900; text-transform: uppercase; }.plan-toolbar > div:last-child { display: flex; gap: .45rem; }
.error-toast { position: fixed; z-index: 30; right: 1rem; bottom: 1rem; display: flex; align-items: center; gap: .65rem; max-width: min(28rem,calc(100% - 2rem)); padding: .8rem 1rem; border: 1px solid rgba(248,113,113,.4); border-radius: .9rem; color: #fecaca; background: rgba(69,10,10,.94); box-shadow: 0 16px 45px rgba(0,0,0,.35); }.error-toast button { margin-left: auto; border: 0; color: inherit; background: none; }
.change-card, .alternatives button, .title-editor input { color: #ff6400; }
.page-heading p,
.feature-tabs button:not(.active),
.locked-state p,
.empty-state p,
.secondary-button,
.loading-state,
.date-navigator button,
.total-badge span,
.meal-keep-help,
.meal-keep-help span,
.meal-strip article div small,
.meal-strip article button,
.target-heading p,
.calorie-result span,
.result-summary p,
.change-copy span,
.change-saving span,
.detail-title button,
.swap-line span,
.reason,
.alternatives small,
.saved-card-top time,
.saved-title-row > button,
.title-editor button,
.saved-card p,
.saved-actions button:not(:first-child),
.saved-pagination button,
.saved-pagination span { color: #fff; }
.date-navigator button:disabled { color: rgba(255,255,255,.45); }
@media (max-width: 760px) { .lighter-page { padding: .8rem .75rem 9rem; }.lighter-shell { padding: 1rem; }.page-heading { margin-bottom: .8rem; }.page-heading h1 { font-size: 2rem; }.heading-icon { display: none; }.page-heading p { max-width: 22rem; font-size: .82rem; line-height: 1.35; }.feature-tabs { margin-bottom: 1rem; }.plan-columns { grid-template-columns: 1fr; }.meal-strip { display: flex; overflow-x: auto; padding-bottom: .25rem; scroll-snap-type: x mandatory; }.meal-strip.has-plan, .meal-keep-help.has-plan { display: none; }.meal-strip article { min-width: 255px; scroll-snap-align: start; }.calorie-result { gap: .6rem; }.saved-total { padding: .35rem .5rem; font-size: .75rem; }.plan-toolbar { align-items: stretch; }.plan-toolbar > div:first-child { display: none; }.plan-toolbar > div:last-child { width: 100%; }.plan-toolbar button { flex: 1; }.detail-title { align-items: flex-start; }.swap-line { gap: .45rem; }.feature-tabs { width: 100%; }.feature-tabs button { flex: 1; }.day-summary { align-items: flex-start; flex-direction: column; }.day-summary-actions { width: 100%; }.date-navigator { flex: 1; grid-template-columns: 2.35rem minmax(0,1fr) 2.35rem; }.total-badge { flex: 0 0 auto; }.target-card { padding: 1rem; }.generate-button { margin-top: .75rem; } }
@media (max-width: 420px) { .page-heading h1 { font-size: 1.9rem; }.target-heading { align-items: flex-end; }.calorie-result strong { font-size: 1.75rem; }.calorie-result > i { font-size: .8rem; }.detail-content { padding: .9rem; }.swap-line { padding: .7rem; }.saved-section .section-heading { align-items: flex-end; } }
</style>
